import { GraduationCap, Megaphone, Users, type LucideIcon } from 'lucide-react';

import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type { NoticeAudience } from '@/types/instituteNotice';
import { NOTICE_AUDIENCES } from './schemas/instituteNotice.schema';

/**
 * How the institute notice board words itself.
 *
 * Only the audience is spelled out here. The three *kinds* — General, Event,
 * Reminder — take their label and colour from the shared `NOTICE_TYPES` map,
 * because a second copy is how an "Event" ends up green on one screen and amber
 * on the next (CLAUDE.md §3). The board's own union is narrower than that map's
 * keys, which is fine: three of its five.
 */

export const AUDIENCE_LABEL = {
  EVERYONE: 'Everyone',
  STAFF: 'Staff only',
  STUDENTS: 'Students only',
} as const satisfies Record<NoticeAudience, string>;

/**
 * Tones are chosen so the two narrow audiences stand out from the ordinary one:
 * a notice for everybody sits quietly, and a notice that half the institute will
 * never see says so at a glance.
 */
export const AUDIENCE_TONE = {
  EVERYONE: 'accent',
  STAFF: 'info',
  STUDENTS: 'success',
} as const satisfies Record<NoticeAudience, BadgeTone>;

/** Who each audience actually reaches, under the picker. */
export const AUDIENCE_HINT = {
  EVERYONE: 'Every member of staff and every student on the roll.',
  STAFF: 'The staff room only — a meeting, a rota, a salary date. No student sees it.',
  STUDENTS: 'The students only — an exam timetable, a uniform reminder.',
} as const satisfies Record<NoticeAudience, string>;

export const AUDIENCE_OPTIONS: Option<NoticeAudience>[] = NOTICE_AUDIENCES.map((value) => ({
  value,
  label: AUDIENCE_LABEL[value],
}));


/**
 * How each audience is drawn across the board.
 *
 * One table rather than a colour decided at each of the three places an audience
 * appears — the tile, the filter chip and the notice card. The hues say how far
 * a notice reaches: the console's blue for everybody, and a colour of its own
 * for each half of the institute that will never see the other's.
 */
export interface AudienceStyle {
  /** The word on a tile, where "Students only" would wrap. */
  short: string;
  icon: LucideIcon;
  chip: string;
  dot: string;
  stroke: string;
  variant: 'indigo' | 'cyan' | 'emerald';
}

export const AUDIENCE_STYLE = {
  EVERYONE: {
    short: 'Everyone',
    icon: Megaphone,
    chip: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/60',
    dot: 'bg-blue-500',
    stroke: 'stroke-blue-500',
    variant: 'indigo',
  },
  STAFF: {
    short: 'Staff only',
    icon: Users,
    chip: 'bg-violet-50 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-900/60',
    dot: 'bg-violet-500',
    stroke: 'stroke-violet-500',
    variant: 'cyan',
  },
  STUDENTS: {
    short: 'Students only',
    icon: GraduationCap,
    chip: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60',
    dot: 'bg-emerald-500',
    stroke: 'stroke-emerald-500',
    variant: 'emerald',
  },
} as const satisfies Record<NoticeAudience, AudienceStyle>;

/** Widest reach first — the board is read from "everybody" inwards. */
export const AUDIENCE_ORDER: NoticeAudience[] = ['EVERYONE', 'STAFF', 'STUDENTS'];

/** The notice board, as every link into it spells it. */
export const NOTICES_PATH = '/admin/notices';

/**
 * The element id a notice card carries, and the hash a link to one ends in —
 * one spelling, so the dashboard's link and the board's card cannot drift apart.
 */
export function noticeAnchorId(uuid: string): string {
  return `notice-${uuid}`;
}
